// src/components/item-builder/McqMultiModal.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

// فرم خالی برای حالت ایجاد
const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  options: [
    { key: "A", fa: "", en: "" },
    { key: "B", fa: "", en: "" },
  ],
  answerKeys: [],
  correctCount: 1, // ✅ تعداد جواب صحیح
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

// تبدیل آیتم بک‌اند به فرم برای ادیت
const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const rawOptions = Array.isArray(body.options) ? body.options : [];
  const options =
    rawOptions.length > 0
      ? rawOptions.map((o, idx) => ({
          key: o.key || String.fromCharCode(65 + idx),
          fa: o?.label?.fa || "",
          en: o?.label?.en || "",
        }))
      : makeEmptyForm().options;

  const layout = body.layout || "top-bottom";

  const scoring = item.scoring || body.scoring || {};
  const correctCount =
    typeof scoring.correctCount === "number"
      ? scoring.correctCount
      : scoring.answerKeys?.length || 1;
  const answerKeys = Array.isArray(scoring.answerKeys)
    ? scoring.answerKeys.map(String)
    : [];

  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    options,
    answerKeys,
    correctCount,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function McqMultiModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) setForm(itemToForm(item));
    else setForm(makeEmptyForm());
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const addOption = () => {
    const nextKey = String.fromCharCode(65 + form.options.length);
    setForm((f) => ({
      ...f,
      options: [...f.options, { key: nextKey, fa: "", en: "" }],
    }));
  };

  const removeOption = (idx) => {
    if (form.options.length <= 2) return; // حداقل ۲ گزینه
    const removedKey = form.options[idx].key;
    const nextOptions = form.options.filter((_, i) => i !== idx);

    setForm((f) => ({
      ...f,
      options: nextOptions,
      answerKeys: f.answerKeys.filter((k) => k !== removedKey),
    }));
  };

  const toggleAnswer = (key) => {
    setForm((f) => {
      const has = f.answerKeys.includes(key);

      if (!has && f.answerKeys.length >= f.correctCount) {
        toast.error(
          t(
            "mcq_multi.toasts.max_answers",
            `حداکثر ${f.correctCount} جواب صحیح می‌توانید انتخاب کنید`,
          ),
        );
        return f;
      }

      return {
        ...f,
        answerKeys: has
          ? f.answerKeys.filter((k) => k !== key)
          : [...f.answerKeys, key],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("mcq_multi.toasts.stem_required"));
      return;
    }
    if (form.options.filter((o) => (o.fa || o.en)?.trim()).length < 2) {
      toast.error(t("mcq_multi.toasts.min_options"));
      return;
    }
    if (form.answerKeys.length < 1) {
      toast.error(t("mcq_multi.toasts.min_answers"));
      return;
    }
    if (form.answerKeys.length !== form.correctCount) {
      toast.error(
        t(
          "mcq_multi.toasts.answer_count_mismatch",
          `باید دقیقاً ${form.correctCount} جواب صحیح انتخاب شود`,
        ),
      );
      return;
    }

    const payload = {
      kind: "mcq_multi",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        options: form.options.map((o) => ({
          key: o.key,
          label: {
            fa: o.fa || undefined,
            en: o.en || undefined,
          },
        })),
        layout: form.layout,
        correctCount: form.correctCount,
      },
      scoring: {
        mode: "all_required",
        answerKeys: form.answerKeys,
      },
      points: Number(form.points) || 1,
      status: form.status || "published",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t(
              "mcq_multi.toasts.updated",
              "سؤال چندگزینه‌ای با موفقیت ویرایش شد",
            )
          : t(
              "mcq_multi.toasts.created",
              "سؤال چندگزینه‌ای با موفقیت ساخته شد",
            ),
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_multi.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("mcq_multi.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("mcq_multi.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("mcq_multi.stem_en_ph")}
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("mcq_multi.layout")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "right-left", "left-right"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, layout: e.target.value }))
                  }
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`mcq_multi.layout_${layout}`)}
                </label>
              </div>
            ),
          )}
        </div>
      </div>

      {/* Options */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("mcq_multi.options")}
          </label>
          <button
            type="button"
            onClick={addOption}
            className="text-blue-600 text-sm"
          >
            <Plus size={16} className="inline-block mr-1" />
            {t("mcq_multi.add_option")}
          </button>
        </div>
        <div className="space-y-2">
          {form.options.map((opt, idx) => (
            <div key={opt.key} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">
                  {t("mcq_multi.option_n", { n: opt.key })}
                </span>
                {form.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(idx)}
                    className="p-1 rounded-full text-red-600 hover:bg-red-100"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("mcq_multi.option_fa_ph")}
                  value={opt.fa}
                  onChange={(e) => {
                    const next = [...form.options];
                    next[idx].fa = e.target.value;
                    setForm((f) => ({ ...f, options: next }));
                  }}
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("mcq_multi.option_en_ph")}
                  value={opt.en}
                  onChange={(e) => {
                    const next = [...form.options];
                    next[idx].en = e.target.value;
                    setForm((f) => ({ ...f, options: next }));
                  }}
                />
              </div>

              <label className="inline-flex items-center gap-2 text-sm mt-2">
                <input
                  type="checkbox"
                  checked={form.answerKeys.includes(opt.key)}
                  onChange={() => toggleAnswer(opt.key)}
                  className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span>{t("mcq_multi.correct_mark")}</span>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_multi.correct_count", "تعداد جواب صحیح")}
          </label>
          <input
            type="number"
            min={1}
            max={form.options.length}
            className="w-full border rounded-lg px-3 py-2"
            value={form.correctCount}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                correctCount: Number(e.target.value),
                answerKeys: f.answerKeys.slice(0, Number(e.target.value)), // ✨
              }))
            }
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_multi.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_multi.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_multi.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
